/* eslint-disable no-console */
import React, { useEffect, useRef, useState } from 'react';
import { Upload, Camera } from 'lucide-react';
import { loadModules } from 'esri-loader';
import '../../../assets/loader/loader.css';
import './ArcgisMap.css';
import { IntlMessages } from '../../../helper/langHelper';
import { mapControlsStyles } from './styles/mapControlsStyles';
import { svgIcons } from './styles/svgIcons';
import { mapHelpers } from './styles/mapHelpers';
import { ArchitecturalShapeTool, ImageImportTool } from './mapTools';
import { handleCaptureAndReturn, handleDownload } from './mapTools/imageCapture/imageCaptureHelper';

// Constants
const ARCGIS_API_URL = 'https://js.arcgis.com/4.26/';
const GEOCODING_SERVICE_URL = process.env.REACT_APP_GEOCODING_SERVICE_URL;
const ARCGIS_API_KEY = process.env.REACT_APP_ARCGIS_API_KEY;

// Component for loading spinner
const LoadingSpinner = () => {
  return (
    <div className="loading-spinner-container">
      <span className="loader"></span>
      <span className="loading-text">
        <IntlMessages id="map.preparing.message" />
      </span>
    </div>
  );
};

// Main component
const ArcgisMap = ({
  address,
  cords,
  bushfireData,
  isMultiScreenshotMode = false,
  onScreenshotCaptured,
  isAdmin = false,
}) => {
  const mapRef = useRef();
  const viewRef = useRef(null);
  const mapContainerRef = useRef();
  const [loading, setLoading] = useState(true);
  const [baseMapType, setBaseMapType] = useState('topo');
  const [overlayVisibility, setOverlayVisibility] = useState({});
  const [isExpanded, setIsExpanded] = useState(false);

  const [isArchitecturalToolActive, setIsArchitecturalToolActive] = useState(false);
  const [architecturalShapes, setArchitecturalShapes] = useState([]);

  const [isImageToolActive, setImageToolActive] = useState(false);
  const [isScreenshotLoading, setIsScreenshotLoading] = useState(false);

  const captureScreenshotInternal = async () => {
    if (!viewRef.current) {
      throw new Error('Map view is not ready');
    }

    try {
      // Ensure map is ready for capture
      await ensureMapReadyForCapture();

      // Capture the screenshot
      const base64Screenshot = await handleCaptureAndReturn(viewRef);

      return base64Screenshot;
    } catch (error) {
      console.error('Screenshot capture error:', error);
      throw error;
    }
  };

  // MODIFIED: Expose capture function via callback when view is ready
  useEffect(() => {
    if (viewRef.current && onScreenshotCaptured) {
      // Pass the capture function to parent
      onScreenshotCaptured(captureScreenshotInternal);
    }
  }, [viewRef.current, onScreenshotCaptured]);

  const handleScreenshot = async () => {
    if (isScreenshotLoading) return;

    try {
      if (!viewRef.current) {
        alert('Map is not ready for screenshot. Please wait for the map to fully load.');
        return;
      }

      // Start loading state
      setIsScreenshotLoading(true);

      // Ensure all layers are loaded and visible
      await ensureMapReadyForCapture();

      await handleDownload(viewRef);
    } catch (error) {
      console.error('Screenshot error:', error);
      alert(`Screenshot failed: ${error.message}. Please try again.`);
    } finally {
      // Always stop loading state
      setIsScreenshotLoading(false);
    }
  };

  // Helper function to ensure map is ready for capture
  const ensureMapReadyForCapture = async () => {
    if (!viewRef.current) return;

    try {
      // Wait for view to be stationary (no animations)
      await viewRef.current.when(() => viewRef.current.stationary);

      // Wait a bit more for all graphics to render
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Force a redraw
      viewRef.current.tryFocus();
    } catch (error) {
      console.warn('Could not ensure map readiness:', error);
    }
  };

  const toggleImageTool = () => {
    setImageToolActive(!isImageToolActive);
  };

  // Store architectural shape graphics separately
  const architecturalGraphicsRef = useRef([]);

  const handleShapeDeleted = (graphic) => {
    console.log('Shape deleted:', graphic);
    setArchitecturalShapes((prev) => prev.filter((s) => s.graphic !== graphic));

    const index = architecturalGraphicsRef.current.indexOf(graphic);
    if (index > -1) {
      architecturalGraphicsRef.current.splice(index, 1);
    }

    console.log('Shape removed from tracking');
  };

  const toggleArchitecturalTool = () => {
    setIsArchitecturalToolActive((prev) => !prev);
  };

  // Enhanced toggle expand/shrink functionality
  const toggleExpand = () => {
    const container = mapContainerRef.current;

    if (!isExpanded) {
      // Expanding
      container.classList.add('expanding');
      setIsExpanded(true);

      // Add body class to prevent scrolling when expanded
      document.body.style.overflow = 'hidden';

      setTimeout(() => {
        container.classList.remove('expanding');
        if (viewRef.current) {
          viewRef.current.resize();
        }
      }, 300);
    } else {
      // Shrinking
      container.classList.add('shrinking');
      setIsExpanded(false);

      // Restore body scrolling
      document.body.style.overflow = '';

      setTimeout(() => {
        container.classList.remove('shrinking');
        if (viewRef.current) {
          viewRef.current.resize();
        }
      }, 300);
    }
  };

  // Handle escape key to close expanded view
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isExpanded) {
        toggleExpand();
      }
    };

    if (isExpanded) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isExpanded]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Restore body scroll on unmount
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    let view;
    let bushfireGraphics = [];
    let modules = {};
    let bushfireOverlayLayer = null;

    // Function to create a polygon graphic
    const createPolygonGraphic = (rings, options = {}, zIndex = 0) => {
      const { Graphic, SimpleFillSymbol } = modules;

      const polygon = {
        type: 'polygon',
        rings: rings,
      };

      let symbol;

      if (options.patternFill) {
        const patternColor = options.patternColor || '#f46262';
        const colorWithOpacity = Array.isArray(patternColor)
          ? [patternColor[0], patternColor[1], patternColor[2], 0.5]
          : mapHelpers.hexToRgba(patternColor, 0.5);

        symbol = new SimpleFillSymbol({
          color: colorWithOpacity,
          outline: {
            color: options.outlineColor || '#000000',
            width: options.outlineWidth || 2,
          },
          style: options?.patternStyle,
        });
      } else {
        const fillColor = options.fillColor || [0, 0, 0, 0.1];
        const colorWithOpacity = Array.isArray(fillColor)
          ? [fillColor[0], fillColor[1], fillColor[2], 0.5]
          : mapHelpers.hexToRgba(fillColor, 0.5);

        symbol = {
          type: 'simple-fill',
          color: colorWithOpacity,
          outline: {
            color: options.outlineColor || '#000000',
            width: options.outlineWidth || 2,
          },
        };
      }

      if (zIndex > 0) {
        symbol.zIndex = zIndex;
      }

      return new Graphic({
        geometry: polygon,
        symbol: symbol,
        attributes: {
          title: options.title || 'Polygon',
          description: options.description || '',
          overlayType: options.overlayType || '',
          category: options.category || '',
          overlayName: options.overlayName || '',
          zIndex: zIndex,
        },
        popupTemplate: {
          title: '{title}',
          content: '{description}',
        },
      });
    };

    // Update legend content
    const updateLegendContent = (legendContainer) => {
      const header = legendContainer.querySelector('.legend-header');
      legendContainer.innerHTML = '';
      legendContainer.appendChild(header);

      if (!bushfireData || bushfireData.length === 0) {
        const noDataMsg = document.createElement('div');
        noDataMsg.className = 'legend-empty';
        noDataMsg.textContent = 'No overlay data available';
        legendContainer.appendChild(noDataMsg);
        return;
      }

      const categories = {};

      bushfireData.forEach((overlay) => {
        if (
          overlay.coordinates
          //  && overlay.applicable
        ) {
          overlay.coordinates.forEach((item) => {
            const category = overlay?.overlayName;
            if (!categories[category]) {
              categories[category] = new Set();
            }

            if (!categories[category].items) {
              categories[category].items = [];
            }

            const existingItemIndex = categories[category].items.findIndex(
              (existingItem) => existingItem.overlayType === item.overlayType
            );

            if (existingItemIndex === -1) {
              categories[category].items.push({
                overlayType: item.overlayType,
                colorPattern: item.colorCode?.pattern || overlay?.colorCode?.pattern,
                colorCode: item.colorCode?.color || overlay?.colorCode?.color || '#f69292',
              });

              categories[category].add(item.overlayType);
            }
          });
        }
      });

      Object.keys(categories)
        .sort()
        .forEach((category) => {
          const categoryDiv = document.createElement('div');
          categoryDiv.className = 'legend-category';

          const categoryToggle = document.createElement('div');
          categoryToggle.className = 'legend-toggle-icon';
          categoryToggle.setAttribute('role', 'button');
          categoryToggle.setAttribute('tabindex', '0');
          categoryToggle.setAttribute('aria-label', `Toggle visibility of ${category}`);

          const isCategoryVisible = !Object.keys(overlayVisibility).some(
            (key) => key.startsWith(`${category}:`) && !overlayVisibility[key]
          );

          categoryToggle.innerHTML = isCategoryVisible ? svgIcons.eyeOpen : svgIcons.eyeClosed;

          categoryToggle.addEventListener('click', () => {
            const newVisibility = { ...overlayVisibility };

            categories[category].items.forEach((item) => {
              const key = `${category}:${item.overlayType}`;
              newVisibility[key] = !isCategoryVisible;
            });

            setOverlayVisibility(newVisibility);
            renderOverlayData(newVisibility);
            updateLegendContent(legendContainer);
          });

          categoryToggle.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              categoryToggle.click();
            }
          });

          const categoryHeader = document.createElement('div');
          categoryHeader.className = 'category-header';
          categoryHeader.appendChild(categoryToggle);

          const categoryLabel = document.createElement('span');
          categoryLabel.textContent = category;
          categoryHeader.appendChild(categoryLabel);

          categoryDiv.appendChild(categoryHeader);

          categories[category].items
            .sort((a, b) => a.overlayType.localeCompare(b.overlayType))
            .forEach((item) => {
              const itemDiv = document.createElement('div');
              itemDiv.className = 'legend-item';

              const overlayKey = `${category}:${item.overlayType}`;
              const isVisible = overlayVisibility[overlayKey] !== false;

              const itemToggle = document.createElement('div');
              itemToggle.className = 'legend-item-toggle';
              itemToggle.setAttribute('role', 'button');
              itemToggle.setAttribute('tabindex', '0');
              itemToggle.setAttribute('aria-label', `Toggle visibility of ${item.overlayType}`);

              itemToggle.innerHTML = isVisible ? svgIcons.eyeOpenSmall : svgIcons.eyeClosedSmall;

              itemToggle.addEventListener('click', () => {
                const newVisibility = { ...overlayVisibility };
                newVisibility[overlayKey] = !isVisible;
                setOverlayVisibility(newVisibility);
                renderOverlayData(newVisibility);
                updateLegendContent(legendContainer);
              });

              itemToggle.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  itemToggle.click();
                }
              });

              const swatch = document.createElement('div');
              swatch.className = 'legend-swatch';

              if (item.colorPattern && item.colorPattern !== 'solid') {
                swatch.style.background = `repeating-linear-gradient(45deg, ${mapHelpers.rgbaToString(item.colorCode || '#f69292')}, ${mapHelpers.rgbaToString(item.colorCode || '#f69292')} 5px, transparent 5px, transparent 10px)`;
              } else {
                swatch.style.backgroundColor = mapHelpers.rgbaToString(item.colorCode || '#f69292');
              }
              swatch.style.border = `2px solid ${item.colorCode || '#f69292'}`;

              if (!isVisible) {
                swatch.style.opacity = '0.4';
              }

              const label = document.createElement('div');
              label.className = 'legend-label';
              label.textContent = item.overlayType;

              itemDiv.appendChild(itemToggle);
              itemDiv.appendChild(swatch);
              itemDiv.appendChild(label);
              categoryDiv.appendChild(itemDiv);

              if (!isVisible) {
                itemDiv.style.opacity = '0.7';
              }
            });

          legendContainer.appendChild(categoryDiv);
        });
    };

    // Create basemap toggle UI component
    const createBasemapToggle = (mapControlsContainer, map) => {
      const basemapToggleContainer = document.createElement('div');
      basemapToggleContainer.className = 'control-button basemap-toggle';
      basemapToggleContainer.setAttribute('role', 'button');
      basemapToggleContainer.setAttribute('aria-expanded', 'false');
      basemapToggleContainer.setAttribute('aria-haspopup', 'true');
      basemapToggleContainer.setAttribute('tabindex', '0');
      basemapToggleContainer.setAttribute('aria-label', 'Toggle map type options');

      basemapToggleContainer.innerHTML = `
        ${svgIcons.mapLayers}
        <span class="control-label map_control">Map</span>
      `;

      const basemapDropdown = document.createElement('div');
      basemapDropdown.className = 'control-dropdown basemap-dropdown';
      basemapDropdown.setAttribute('role', 'menu');
      basemapDropdown.style.display = 'none';

      const options = [
        { id: 'satellite', label: 'Satellite' },
        { id: 'streets', label: 'Streets' },
        { id: 'topo', label: 'Topographic' },
      ];

      options.forEach((option) => {
        const optionButton = document.createElement('button');
        optionButton.className = `control-option ${option.id === baseMapType ? 'active' : ''}`;
        optionButton.setAttribute('role', 'menuitem');
        optionButton.setAttribute('tabindex', '-1');
        optionButton.textContent = option.label;

        optionButton.addEventListener('click', () => {
          document.querySelectorAll('.control-option').forEach((el) => {
            el.classList.remove('active');
          });
          optionButton.classList.add('active');

          map.basemap = option.id;
          setBaseMapType(option.id);

          basemapDropdown.style.display = 'none';
          basemapToggleContainer.setAttribute('aria-expanded', 'false');
        });

        basemapDropdown.appendChild(optionButton);
      });

      mapControlsContainer.appendChild(basemapToggleContainer);
      mapControlsContainer.appendChild(basemapDropdown);

      return { basemapToggleContainer, basemapDropdown };
    };

    // Create legend toggle UI component
    const createLegendToggle = (mapControlsContainer) => {
      const legendToggleContainer = document.createElement('div');
      legendToggleContainer.className = 'control-button legend-toggle';
      legendToggleContainer.setAttribute('role', 'button');
      legendToggleContainer.setAttribute('aria-expanded', 'false');
      legendToggleContainer.setAttribute('aria-haspopup', 'true');
      legendToggleContainer.setAttribute('tabindex', '0');
      legendToggleContainer.setAttribute('aria-label', 'Toggle legend');

      legendToggleContainer.innerHTML = `
        ${svgIcons.legend}
        <span class="control-label legend_control">Legend</span>
      `;

      const legendDropdown = document.createElement('div');
      legendDropdown.className = 'control-dropdown legend-dropdown';
      legendDropdown.setAttribute('role', 'menu');
      legendDropdown.style.display = 'none';

      const legendHeader = document.createElement('div');
      legendHeader.className = 'legend-header';
      legendHeader.innerHTML = 'Legend';
      legendDropdown.appendChild(legendHeader);

      mapControlsContainer.appendChild(legendToggleContainer);
      mapControlsContainer.appendChild(legendDropdown);

      return { legendToggleContainer, legendDropdown };
    };

    // Setup map controls with event handling
    const setupMapControls = (map, view) => {
      const mapControlsContainer = document.createElement('div');
      mapControlsContainer.className = 'map-controls-container';
      mapControlsContainer.setAttribute('role', 'region');
      mapControlsContainer.setAttribute('aria-label', 'Map controls');

      const { basemapToggleContainer, basemapDropdown } = createBasemapToggle(
        mapControlsContainer,
        map
      );
      const { legendToggleContainer, legendDropdown } = createLegendToggle(mapControlsContainer);

      // Event handlers
      basemapToggleContainer.addEventListener('click', () => {
        const isExpanded = basemapToggleContainer.getAttribute('aria-expanded') === 'true';
        basemapToggleContainer.setAttribute('aria-expanded', !isExpanded);
        basemapDropdown.style.display = isExpanded ? 'none' : 'block';

        legendDropdown.style.display = 'none';
        legendToggleContainer.setAttribute('aria-expanded', 'false');
      });

      legendToggleContainer.addEventListener('click', () => {
        const isExpanded = legendToggleContainer.getAttribute('aria-expanded') === 'true';
        legendToggleContainer.setAttribute('aria-expanded', !isExpanded);
        legendDropdown.style.display = isExpanded ? 'none' : 'block';

        basemapDropdown.style.display = 'none';
        basemapToggleContainer.setAttribute('aria-expanded', 'false');

        if (!isExpanded) {
          updateLegendContent(legendDropdown);
        }
      });

      // Keyboard navigation
      basemapToggleContainer.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          basemapToggleContainer.click();
        } else if (e.key === 'Escape') {
          basemapDropdown.style.display = 'none';
          basemapToggleContainer.setAttribute('aria-expanded', 'false');
          basemapToggleContainer.focus();
        } else if (
          e.key === 'ArrowDown' &&
          basemapToggleContainer.getAttribute('aria-expanded') === 'true'
        ) {
          e.preventDefault();
          basemapDropdown.querySelector('button').focus();
        }
      });

      legendToggleContainer.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          legendToggleContainer.click();
        } else if (e.key === 'Escape') {
          legendDropdown.style.display = 'none';
          legendToggleContainer.setAttribute('aria-expanded', 'false');
          legendToggleContainer.focus();
        }
      });

      basemapDropdown.addEventListener('keydown', (e) => {
        const options = Array.from(basemapDropdown.querySelectorAll('button'));
        const index = options.indexOf(document.activeElement);

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          options[(index + 1) % options.length].focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          options[(index - 1 + options.length) % options.length].focus();
        } else if (e.key === 'Escape') {
          basemapDropdown.style.display = 'none';
          basemapToggleContainer.setAttribute('aria-expanded', 'false');
          basemapToggleContainer.focus();
        }
      });

      // Close dropdowns when clicking outside
      document.addEventListener('click', (e) => {
        if (!mapControlsContainer.contains(e.target)) {
          basemapDropdown.style.display = 'none';
          basemapToggleContainer.setAttribute('aria-expanded', 'false');
          legendDropdown.style.display = 'none';
          legendToggleContainer.setAttribute('aria-expanded', 'false');
        }
      });

      view?.ui?.add(mapControlsContainer, 'bottom-left');

      return { legendDropdown };
    };

    // Enhanced render overlay data with proper layer management
    // const renderOverlayData = (visibilityState = overlayVisibility) => {
    //   // Clear existing graphics
    //   bushfireGraphics.forEach((graphic) => {
    //     view.graphics.remove(graphic);
    //   });
    //   bushfireGraphics = [];

    //   if (bushfireData && Array.isArray(bushfireData)) {
    //     const allGraphics = [];

    //     bushfireData.forEach((overlay) => {
    //       if (overlay.coordinates && overlay.applicable) {
    //         overlay.coordinates.forEach((overlayItem) => {
    //           const category = overlay?.overlayName;
    //           const overlayKey = `${category}:${overlayItem.overlayType}`;

    //           const isVisible = visibilityState[overlayKey] !== false;

    //           if (isVisible) {
    //             overlayItem.intersection.forEach((polygon) => {
    //               const area = mapHelpers.calculatePolygonArea(polygon);
    //               const pointCount = polygon[0]?.length || 0;
    //               const zIndex = Math.round(1000000 / (area + 1));

    //               const graphic = createPolygonGraphic(
    //                 polygon,
    //                 {
    //                   fillColor: overlayItem?.colorCode?.color || '#f69292',
    //                   outlineColor: overlayItem?.colorCode?.color || '#f69292',
    //                   outlineWidth: 2,
    //                   patternFill: overlayItem?.colorCode?.pattern || false,
    //                   patternStyle: overlayItem?.colorCode?.pattern || null,
    //                   patternColor: overlayItem?.colorCode?.color || '#f69292',
    //                   title: overlay.overlayName,
    //                   overlayType: overlayItem.overlayType,
    //                   category: category,
    //                   overlayName: overlay.overlayName,
    //                 },
    //                 zIndex
    //               );

    //               graphic.attributes.area = area;
    //               graphic.attributes.pointCount = pointCount;
    //               graphic.attributes.intersectionLength = overlayItem.intersection.length;
    //               graphic.attributes.overlayKey = overlayKey;

    //               allGraphics.push(graphic);
    //             });
    //           }
    //         });
    //       }
    //     });

    //     // Sort by area for proper layering
    //     allGraphics.sort((a, b) => a.attributes.area - b.attributes.area);

    //     // Add graphics to view with proper attributes for capture
    //     allGraphics.forEach((graphic) => {
    //       // Add metadata for better capture identification
    //       graphic.attributes.isOverlayGraphic = true;
    //       graphic.attributes.captureLayer = 'bushfire-overlay';

    //       bushfireGraphics.push(graphic);
    //       view.graphics.add(graphic);
    //     });
    //   }
    // };

    const ensureArchitecturalLayersOnTop = () => {
      const architecturalLayer = view?.map?.findLayerById('architectural-shapes');
      const measurementLayer = view?.map?.findLayerById('shape-measurements');
      const imageOverlayLayer = view?.map?.findLayerById('Image Overlay');

      const totalLayers = view?.map?.layers?.length;

      // Ensure order from bottom to top:
      // 1. Bushfire overlays (bottom)
      // 2. Other layers
      // 3. Image overlay
      // 4. Architectural shapes
      // 5. Measurements (top)

      if (imageOverlayLayer) {
        view.map.reorder(imageOverlayLayer, totalLayers - 3);
      }
      if (architecturalLayer) {
        view.map.reorder(architecturalLayer, totalLayers - 2);
      }
      if (measurementLayer) {
        view.map.reorder(measurementLayer, totalLayers - 1);
      }
    };

    const renderOverlayData = async (visibilityState = overlayVisibility) => {
      // Create or get the bushfire overlay layer
      if (!bushfireOverlayLayer) {
        const [GraphicsLayer] = await loadModules(['esri/layers/GraphicsLayer']);

        bushfireOverlayLayer = view?.map?.findLayerById('bushfire-overlays');
        if (!bushfireOverlayLayer) {
          bushfireOverlayLayer = new GraphicsLayer({
            id: 'bushfire-overlays',
            title: 'Bushfire Overlays',
            listMode: 'hide',
          });

          // Add the layer at index 0 (bottom of the stack)
          view?.map?.add(bushfireOverlayLayer, 0);
        }
      }

      // Clear existing graphics from the bushfire layer only
      bushfireOverlayLayer.removeAll();
      bushfireGraphics = [];

      if (bushfireData && Array.isArray(bushfireData)) {
        const allGraphics = [];

        bushfireData.forEach((overlay) => {
          if (
            overlay.coordinates
            // && overlay.applicable
          ) {
            overlay.coordinates.forEach((overlayItem) => {
              const category = overlay?.overlayName;
              const overlayKey = `${category}:${overlayItem.overlayType}`;

              const isVisible = visibilityState[overlayKey] !== false;

              if (isVisible) {
                overlayItem.intersection.forEach((polygon) => {
                  const area = mapHelpers.calculatePolygonArea(polygon);
                  const pointCount = polygon[0]?.length || 0;
                  const zIndex = Math.round(1000000 / (area + 1));

                  const graphic = createPolygonGraphic(
                    polygon,
                    {
                      fillColor: overlayItem?.colorCode?.color || '#f69292',
                      outlineColor: overlayItem?.colorCode?.color || '#f69292',
                      outlineWidth: 2,
                      patternFill: overlayItem?.colorCode?.pattern || false,
                      patternStyle: overlayItem?.colorCode?.pattern || null,
                      patternColor: overlayItem?.colorCode?.color || '#f69292',
                      title: overlay.overlayName,
                      overlayType: overlayItem.overlayType,
                      category: category,
                      overlayName: overlay.overlayName,
                    },
                    zIndex
                  );

                  graphic.attributes.area = area;
                  graphic.attributes.pointCount = pointCount;
                  graphic.attributes.intersectionLength = overlayItem.intersection.length;
                  graphic.attributes.overlayKey = overlayKey;

                  allGraphics.push(graphic);
                });
              }
            });
          }
        });

        // Sort by area for proper layering
        allGraphics.sort((a, b) => a.attributes.area - b.attributes.area);

        // Add graphics to the dedicated bushfire layer instead of view.graphics
        allGraphics.forEach((graphic) => {
          graphic.attributes.isOverlayGraphic = true;
          graphic.attributes.captureLayer = 'bushfire-overlay';

          bushfireGraphics.push(graphic);
          bushfireOverlayLayer.add(graphic); // Add to layer instead of view.graphics
        });
      }

      // Ensure architectural layers stay on top
      ensureArchitecturalLayersOnTop();
    };

    // Enhanced display boundaries
    // const displayBoundaries = () => {
    //   const { Graphic } = modules;

    //   const rectangle = {
    //     type: 'polygon',
    //     rings: cords,
    //   };

    //   const rectangleGraphic = new Graphic({
    //     geometry: rectangle,
    //     symbol: {
    //       type: 'simple-fill',
    //       color: [0, 0, 0, 0.1],
    //       outline: {
    //         color: '#ff0000',
    //         width: 2,
    //       },
    //     },
    //     attributes: {
    //       isBoundary: true,
    //       captureLayer: 'boundary',
    //     },
    //   });

    //   view.graphics.add(rectangleGraphic);
    //   renderOverlayData();

    //   (async () => {
    //     try {
    //       await view.goTo(rectangleGraphic.geometry.extent.expand(1.5));
    //       // Ensure view is stationary before allowing screenshots
    //       await view.when(() => view.stationary);
    //     } catch (error) {
    //       console.error('Error adjusting view:', error);
    //     }
    //   })();
    // };

    const displayBoundaries = async () => {
      const { Graphic } = modules;

      // Create boundary layer if it doesn't exist
      let boundaryLayer = view?.map?.findLayerById('boundary-layer');
      if (!boundaryLayer) {
        const [GraphicsLayer] = await loadModules(['esri/layers/GraphicsLayer']);
        boundaryLayer = new GraphicsLayer({
          id: 'boundary-layer',
          title: 'Boundary',
          listMode: 'hide',
        });
        view?.map?.add(boundaryLayer, 0); // Add at bottom
      } else {
        boundaryLayer.removeAll();
      }

      const rectangle = {
        type: 'polygon',
        rings: cords,
      };

      const rectangleGraphic = new Graphic({
        geometry: rectangle,
        symbol: {
          type: 'simple-fill',
          color: [0, 0, 0, 0.1],
          outline: {
            color: '#ff0000',
            width: 2,
          },
        },
        attributes: {
          isBoundary: true,
          captureLayer: 'boundary',
        },
      });

      boundaryLayer.add(rectangleGraphic); // Add to layer instead of view.graphics
      renderOverlayData();

      (async () => {
        try {
          await view.goTo(rectangleGraphic.geometry.extent.expand(1.5));
          await view.when(() => view.stationary);
          ensureArchitecturalLayersOnTop(); // Ensure proper layer order
        } catch (error) {
          console.error('Error adjusting view:', error);
        }
      })();
    };

    // Process address geocoding
    const locateAddress = async () => {
      const { locator } = modules;

      if (!address) return;

      const params = {
        address: {
          SingleLine: address,
        },
      };

      try {
        const results = await locator.addressToLocations(GEOCODING_SERVICE_URL, params);
        if (results.length > 0) {
          showResult(results);
        } else {
          console.error('No location found for address:', address);
          displayBoundaries();
        }
      } catch (error) {
        console.error('Geocoding error:', error);
        displayBoundaries();
      }
    };

    // Enhanced show geocoding result
    const showResult = (results) => {
      const { Graphic } = modules;

      if (results.length) {
        const result = results[0];

        const pointGraphic = new Graphic({
          symbol: {
            type: 'simple-marker',
            color: '#000000',
            size: '8px',
            outline: {
              color: '#ffffff',
              width: '1px',
            },
          },
          geometry: result.location,
          attributes: {
            title: 'Address',
            address: result.address,
            score: result.score,
            isAddressMarker: true,
            captureLayer: 'address-marker',
          },
          popupTemplate: {
            title: '{title}',
            content: `${result.address}<br><br>Coordinates: ${result.location.longitude.toFixed(
              5
            )}, ${result.location.latitude.toFixed(5)}`,
          },
        });

        const rectangle = {
          type: 'polygon',
          rings: cords,
        };

        const rectangleGraphic = new Graphic({
          geometry: rectangle,
          symbol: {
            type: 'simple-fill',
            color: [0, 0, 0, 0.1],
            outline: {
              color: '#ff0000',
              width: 2,
            },
          },
          attributes: {
            isBoundary: true,
            captureLayer: 'boundary',
          },
        });

        view?.graphics?.removeAll();
        view?.graphics?.add(pointGraphic);
        view?.graphics?.add(rectangleGraphic);

        renderOverlayData();

        (async () => {
          try {
            await view.goTo(rectangleGraphic.geometry.extent.expand(1.5));
            // Ensure view is stationary
            await view.when(() => view.stationary);

            view.popup.open({
              features: [pointGraphic],
              location: result.location,
            });
          } catch (error) {
            console.error('Error adjusting view:', error);
          }
        })();
      }
    };

    // Enhanced initialize map with better layer management
    // const initializeMap = async () => {
    //   try {
    //     const [ArcGISMap, MapView, Graphic, locator, esriConfig, SimpleFillSymbol] =
    //       await loadModules(
    //         [
    //           'esri/Map',
    //           'esri/views/MapView',
    //           'esri/Graphic',
    //           'esri/rest/locator',
    //           'esri/config',
    //           'esri/symbols/SimpleFillSymbol',
    //         ],
    //         {
    //           url: ARCGIS_API_URL,
    //           css: true,
    //         }
    //       );

    //     modules = {
    //       ArcGISMap,
    //       MapView,
    //       Graphic,
    //       locator,
    //       esriConfig,
    //       SimpleFillSymbol,
    //     };

    //     esriConfig.apiKey = ARCGIS_API_KEY;

    //     const map = new ArcGISMap({
    //       basemap: baseMapType,
    //     });

    //     view = new MapView({
    //       container: mapRef.current,
    //       map,
    //       center: [152.85, -27.41],
    //       zoom: 16,
    //       popup: {
    //         dockEnabled: false,
    //         autoOpenEnabled: false,
    //       },
    //       ui: {
    //         components: ['attribution'],
    //       },
    //       // Enhanced for better screenshot capture
    //       constraints: {
    //         snapToZoom: false,
    //       },
    //     });

    //     // Wait for view to be fully ready
    //     await view.when();

    //     // Ensure the view is completely loaded
    //     await new Promise((resolve) => {
    //       if (view.ready) {
    //         resolve();
    //       } else {
    //         const handle = view.watch('ready', (ready) => {
    //           if (ready) {
    //             handle.remove();
    //             resolve();
    //           }
    //         });
    //       }
    //     });

    //     viewRef.current = view;
    //     setLoading(false);

    //     mapControlsStyles.injectStyles();
    //     const { legendDropdown } = setupMapControls(map, view);

    //     // Add container class for better capture identification
    //     const mapContainer = mapRef.current;
    //     if (mapContainer) {
    //       mapContainer.setAttribute('data-capture-layer', 'arcgis-map');
    //       mapContainer.classList.add('arcgis-capture-ready');
    //     }

    //     if (address) {
    //       await locateAddress();
    //     } else if (cords && cords.length) {
    //       displayBoundaries();
    //     }

    //     updateLegendContent(legendDropdown);

    //     // Final readiness check
    //     await view.when(() => view.stationary);
    //   } catch (error) {
    //     console.error('Error initializing map:', error);
    //     setLoading(false);
    //   }
    // };

    const initializeMap = async () => {
      try {
        const [ArcGISMap, MapView, Graphic, locator, esriConfig, SimpleFillSymbol, Fullscreen] =
          await loadModules(
            [
              'esri/Map',
              'esri/views/MapView',
              'esri/Graphic',
              'esri/rest/locator',
              'esri/config',
              'esri/symbols/SimpleFillSymbol',
              'esri/widgets/Fullscreen',
            ],
            {
              url: ARCGIS_API_URL,
              css: true,
            }
          );

        modules = {
          ArcGISMap,
          MapView,
          Graphic,
          locator,
          esriConfig,
          SimpleFillSymbol,
        };

        esriConfig.apiKey = ARCGIS_API_KEY;

        const map = new ArcGISMap({
          basemap: baseMapType,
        });

        view = new MapView({
          container: mapRef.current,
          map,
          center: [152.85, -27.41],
          zoom: 16,
          popup: {
            dockEnabled: false,
            autoOpenEnabled: false,
          },
          ui: {
            components: ['attribution'],
          },
          constraints: {
            snapToZoom: false,
          },
        });

        await view.when();

        // IMPORTANT: Set up layer ordering after view is ready
        // This ensures architectural layers are always on top
        view.watch('ready', () => {
          // Reorder layers whenever a new layer is added
          view?.map?.layers?.on('after-add', () => {
            ensureLayerOrder();
          });
        });

        // Function to ensure correct layer ordering
        const ensureLayerOrder = () => {
          const architecturalLayer = view?.map?.findLayerById('architectural-shapes');
          const measurementLayer = view?.map?.findLayerById('shape-measurements');

          if (architecturalLayer) {
            // Move architectural layer to top
            view.map.reorder(architecturalLayer, view?.map?.layers?.length - 1);
          }
          if (measurementLayer) {
            // Move measurement layer to top (above architectural)
            view.map.reorder(measurementLayer, view?.map?.layers?.length - 1);
          }
        };

        await new Promise((resolve) => {
          if (view.ready) {
            resolve();
          } else {
            const handle = view.watch('ready', (ready) => {
              if (ready) {
                handle.remove();
                resolve();
              }
            });
          }
        });

        viewRef.current = view;
        setLoading(false);

        const fullscreen = new Fullscreen({
          view: view,
        });

        view.ui.add(fullscreen, 'top-left');

        mapControlsStyles.injectStyles();
        const { legendDropdown } = setupMapControls(map, view);

        const mapContainer = mapRef.current;
        if (mapContainer) {
          mapContainer.setAttribute('data-capture-layer', 'arcgis-map');
          mapContainer?.classList?.add('arcgis-capture-ready');
        }

        if (address) {
          await locateAddress();
        } else if (cords && cords.length) {
          displayBoundaries();
        }

        updateLegendContent(legendDropdown);

        await view.when(() => view.stationary);

        // Ensure layer order after initial load
        ensureLayerOrder();
      } catch (error) {
        console.error('Error initializing map:', error);
        setLoading(false);
      }
    };

    initializeMap();

    return () => {
      const legendDropdown = document.querySelector('.legend-dropdown');
      if (legendDropdown) {
        legendDropdown.remove();
      }
      architecturalGraphicsRef.current = [];

      // Cleanup view
      if (view) {
        view.destroy();
      }
    };
  }, [address, cords, bushfireData, baseMapType, overlayVisibility]);

  return (
    <div
      className={`map-container ${isExpanded ? 'expanded' : ''}`}
      ref={mapContainerRef}
      data-capture-container="true"
    >
      {loading && <LoadingSpinner />}
      {viewRef.current && !isMultiScreenshotMode && !isAdmin && (
        <div className="tool-controls-container" data-capture-layer="tool-controls">
          <div className="tool-buttons-group">
            <button
              className={`tool-button draw-button ${isArchitecturalToolActive ? 'active' : ''}`}
              onClick={toggleArchitecturalTool}
              title="Toggle Architectural Drawing Tool"
            >
              <div dangerouslySetInnerHTML={{ __html: svgIcons.draw }} />
              <span className="button-text draw_control">Draw</span>
            </button>

            <button
              className={`tool-button import-button ${isImageToolActive ? 'active' : ''}`}
              onClick={toggleImageTool}
              title="Toggle Image Import Tool"
            >
              <Upload size={18} />
              <span className="button-text import_plan_control">Import</span>
            </button>

            {/* <button
              onClick={handleScreenshot}
              title="Capture Complete Map Screenshot"
              className={`tool-button screenshot-button ${isScreenshotLoading ? 'loading' : ''}`}
              disabled={loading || !viewRef.current || isScreenshotLoading}
            >
              {isScreenshotLoading ? (
                <>
                  <span className="button-text">Capturing...</span>
                </>
              ) : (
                <>
                  <Camera size={18} />
                  <span className="button-text">Screenshot</span>
                </>
              )}
            </button> */}
          </div>

          {isImageToolActive && (
            <ImageImportTool
              view={viewRef.current}
              isActive={isImageToolActive}
              onToggle={toggleImageTool}
            />
          )}

          {isArchitecturalToolActive && (
            <ArchitecturalShapeTool
              view={viewRef.current}
              onShapeDeleted={handleShapeDeleted}
              isActive={isArchitecturalToolActive}
              onToggle={toggleArchitecturalTool}
            />
          )}
        </div>
      )}

      <div
        id="arcgis-map-container"
        ref={mapRef}
        className="arcgis-map-container"
        data-capture-layer="arcgis-base-map"
      />
    </div>
  );
};

export default ArcgisMap;
